import * as _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { DropdownButton, MenuItem } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';

import {
  ENTERPRISE_COMPLIANCE,
  ENTERPRISE_PLATFORM,
  ENTERPRISE_RELEASE_MANAGEMENT,
  FREE_PLAN_NAME,
  getSubscriptionPrice,
  isDynamicPlan,
  isEnterpriseTier,
  LEGACY_PREMIUM_PLAN_1,
  LEGACY_PREMIUM_PLAN_2,
  PREMIUM_COMP_SEC_PLAN_NAME,
  PREMIUM_COMPLIANCE_PLAN_NAME,
  PREMIUM_PER_REVISION_PLAN_NAME,
} from '../../../../shared/subscriptions';
import { useDialogContext } from '../../../contexts/DialogContext';
import { window } from '../../../globals';
import { isOrgInEnterpriseTier } from '../../../helpers/subscriptions';
import useUpdateOrgBilling from '../../../hooks/api/billing/useUpdateOrgBilling';
import useConvertToPOCEnterprise from '../../../hooks/api/subscriptions/useConvertToPOCEnterprise';
import useCurrentOrgContractProps from '../../../hooks/subscriptions/useCurrentOrgContractProps';
import { BUTTON_TYPE, ButtonGroup, Icon, icons, LoaderButton } from '../../generic';
import Card from '../../generic/Card/Card';
import LabeledCheckbox from '../../generic/Forms/LabeledCheckbox';
import { getCurrentPlanType } from '../../settings/subscription/utils';
import type { FossaPlanName } from '../../../../shared/subscriptions';
import type { Organization } from '../../../types';

import { EnterpriseBillingOptions } from './EnterpriseBillingOptions';

const membershipTitleMap: Record<FossaPlanName, string> = {
  [FREE_PLAN_NAME]: 'Free',
  [LEGACY_PREMIUM_PLAN_1]: `Deprecated: Premium ($${getSubscriptionPrice(LEGACY_PREMIUM_PLAN_1)}/dev/month)`,
  [LEGACY_PREMIUM_PLAN_2]: `Deprecated: Premium ($${getSubscriptionPrice(LEGACY_PREMIUM_PLAN_2)}/dev/month)`,
  [PREMIUM_COMPLIANCE_PLAN_NAME]: `Premium Compliance ($${getSubscriptionPrice(PREMIUM_COMPLIANCE_PLAN_NAME)}/dev/month)`,
  [PREMIUM_COMP_SEC_PLAN_NAME]: `Premium Compliance + Security ($${getSubscriptionPrice(PREMIUM_COMP_SEC_PLAN_NAME)}/dev/month)`,
  [PREMIUM_PER_REVISION_PLAN_NAME]: 'Premium Per-Revision Pricing (Custom Billing)',
  [ENTERPRISE_COMPLIANCE]: 'Enterprise Compliance',
  [ENTERPRISE_PLATFORM]: 'Enterprise Platform',
  [ENTERPRISE_RELEASE_MANAGEMENT]: 'Enterprise Release Management',
};

/**
 * Helper for narrowing down which tiers to show based on the current plan and feature flags.
 * Exported for testing purposes.
 */
export function getTiersToShow(plan: FossaPlanName) {
  const tiersToHide = [
    // Hide the legacy plans except the one the org is currently on (if on one at all).
    ...[plan !== LEGACY_PREMIUM_PLAN_1 ? [LEGACY_PREMIUM_PLAN_1] : []],
    ...[plan !== LEGACY_PREMIUM_PLAN_2 ? [LEGACY_PREMIUM_PLAN_2] : []],
  ].flat();

  // Include dynamic plan
  if (isDynamicPlan(plan)) {
    let type;
    if (window?.user?.organization) {
      type = getCurrentPlanType(window?.user?.organization);
    }
    membershipTitleMap[plan] = `Dynamic ${type} Plan (Managed in Stripe)`;
  }
  return _.omit(membershipTitleMap, tiersToHide);
}

/**
 * An array of all non-free tier names. This will be used to check for billing change confirmation and if
 * custom billing should be enabled by default.
 */
const premiumBillingTiers = Object.keys(_.omit(membershipTitleMap, 'free-seat'));

type ChangeSubscriptionCardProps = {
  org: Organization;
};

const ChangeSubscriptionCard: VoidFunctionComponent<ChangeSubscriptionCardProps> = ({ org }) => {
  const [selectedTier, setSelectedTier] = useState<FossaPlanName>(org.stripe_plan);
  const [isCustomBillingChecked, setCustomBillingChecked] = useState(org.custom_billing);
  const { isLoading, error, isError, mutate: submitPlanUpdateRequest } = useUpdateOrgBilling();
  const { isLoading: isUpdatingPocPlan, mutate: submitPocRequest } = useConvertToPOCEnterprise();
  const { onConfirm } = useDialogContext();
  const {
    contractProps,
    updateContractProperty,
    isLoading: isContractLoading,
    errors: asyncContractErrors,
    isDirty: areContractPropsDirty,
  } = useCurrentOrgContractProps();

  const status = {
    isLoading: isContractLoading || isLoading || isUpdatingPocPlan,
    errors: [
      ...(isError ? [error?.data?.message] : []),
      ...(asyncContractErrors ?? []),
    ],
  };

  useEffect(() => {
    setSelectedTier(org.stripe_plan);
    setCustomBillingChecked(org.custom_billing);
  }, [org]);

  useEffect(() => {
    setSelectedTier(org.stripe_plan);
    setCustomBillingChecked(org.custom_billing);
  }, [org]);

  const onUpgradeOrgToPOCPlan = useCallback(() => {
    onConfirm({
      title: `Confirm Billing: ${org.title}`,
      body: (
        <div>
          <div>
            You are changing the subscription for <b>{org.title}</b> to the <b>POC Enterprise</b>{' '}
            plan.
          </div>
        </div>
      ),
      onConfirm: submitPocRequest,
    });
  }, [onConfirm, org.title, submitPocRequest]);

  const onUpgradeOrg = useCallback(() => {
    // If we're upgrading to a premium tier and custom billing is not enabled, we want to display the modal so that
    // we don't accidentally charge the customer.
    if (premiumBillingTiers.includes(selectedTier) && !isCustomBillingChecked) {
      onConfirm({
        title: `Confirm Billing: ${org.title}`,
        body: (
          <div>
            <div>
              You are changing the subscription for <b>{org.title}</b> to the <b>{membershipTitleMap[selectedTier]}</b>{' '}
              plan.
            </div>
            <br />
            <div>
              Are you <b>sure</b> you&apos;d like to change this organization to a premium tier without custom billing
              enabled? This could cause additional charges to the customer.
            </div>
          </div>
        ),
        onConfirm: () => submitPlanUpdateRequest({ plan: selectedTier, custom_billing: false }),
      });
      return;
    }

    if (selectedTier === PREMIUM_PER_REVISION_PLAN_NAME) {
      onConfirm({
        title: `Confirm Billing: ${org.title}`,
        body: 'Per-Revision pricing is a special billing plan for customers using per-revision pricing. Please ensure that this organization is a per-revision pricing customer before enabling.',
        onConfirm: () => submitPlanUpdateRequest({ plan: selectedTier, custom_billing: true }),
      });

      return;
    }

    if (isEnterpriseTier({ plan: org.stripe_plan, stripePriceLimit: org.stripePriceLimit })) {
      onConfirm({
        title: `Confirm Billing: ${org.title}`,
        body: (
          <div>
            You are changing the subscription for <b>{org.title}</b> to the <b>{membershipTitleMap[selectedTier]}</b>{' '}
            plan. The following limits are being applied:
            <ul>
              <li>
                Release Group Limit:{' '}
                {contractProps?.unlimitedReleaseGroups
                  ? 'Unlimited'
                  : (contractProps?.releaseGroupLimit ?? 'Not Specified')}
              </li>
              <li>
                Contributor Limit:{' '}
                {contractProps?.unlimitedContributors
                  ? 'Unlimited'
                  : (contractProps?.contributorLimit ?? 'Not Specified')}
              </li>
            </ul>
            Do you wish to continue?
          </div>
        ),
        onConfirm: () => submitPlanUpdateRequest({ plan: selectedTier, ...contractProps }),
      });

      return;
    }

    onConfirm({
      title: `Confirm Billing: ${org.title}`,
      body: (
        <div>
          You are changing the subscription for <b>{org.title}</b> to the <b>{membershipTitleMap[selectedTier]}</b>{' '}
          plan. Do you wish to continue?
        </div>
      ),
      onConfirm: () => submitPlanUpdateRequest({ plan: selectedTier, custom_billing: isCustomBillingChecked }),
    });
  }, [selectedTier, isCustomBillingChecked, onConfirm, org.title, submitPlanUpdateRequest, contractProps]);

  const isEnterpriseTierSelected = isEnterpriseTier({ plan: selectedTier });
  const tiersToShow = getTiersToShow(org.stripe_plan);
  const isDirty =
    selectedTier !== org.stripe_plan || isCustomBillingChecked !== org.custom_billing || areContractPropsDirty;

  return (
    <Card isFullWidth padding="medium">
      <Card.Header>Change current org subscription</Card.Header>
      <Card.Content>
        {status.errors &&
            !_.isEmpty(status.errors) &&
            status.errors.map((err) => <div className="alert alert-error">{err}</div>)}
        <br />
        <DropdownButton
          id="change-current-org-subscription-button"
          title={membershipTitleMap[selectedTier]}
            // @ts-expect-error This works even though the typings complain.
          onSelect={(orgLevel: string) => {
            setSelectedTier(orgLevel as FossaPlanName);
            // We default the Custom Billing checkbox to true if it's a non-free tier.
            setCustomBillingChecked(premiumBillingTiers.includes(orgLevel));
          }}
          >
          {/* eslint-disable-next-line you-dont-need-lodash-underscore/map */}
          {_.map(tiersToShow, (title, planId) => (
            <MenuItem key={planId} eventKey={planId}>
              {title}
            </MenuItem>
          ))}
        </DropdownButton>
        <br />
        {isEnterpriseTierSelected &&
            (status.isLoading ? (
              <Icon hasSpin icon={icons.circleO} />
            ) : (
              <EnterpriseBillingOptions contractProps={contractProps} updateContractProperty={updateContractProperty} />
            ))}
        {!isEnterpriseTierSelected && (
        <LabeledCheckbox
          label="Custom Billing"
          isChecked={isCustomBillingChecked}
          isDisabled={selectedTier === PREMIUM_PER_REVISION_PLAN_NAME}
          onChange={(checkEvent) => setCustomBillingChecked(checkEvent.target.checked)}
            />
        )}
        <br />
        <ButtonGroup>
          <LoaderButton
            onClick={onUpgradeOrg}
            isDisabled={!isDirty || status.isLoading}
            isLoading={status.isLoading}
            type="slate"
            >
            Submit
          </LoaderButton>
          <LoaderButton
            onClick={onUpgradeOrgToPOCPlan}
            isDisabled={isOrgInEnterpriseTier() || status.isLoading}
            isLoading={status.isLoading}
            type={BUTTON_TYPE.ERROR}
          >
            POC Enterprise
          </LoaderButton>
        </ButtonGroup>
      </Card.Content>
    </Card>
  );
};

export default ChangeSubscriptionCard;
